import React, { useCallback, useEffect, useState } from 'react';
import useFetch from 'use-http';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { DateField, Field, SelectField } from './Master';
import ARPaymentHistoryDialog from './ARPaymentHistoryDialog';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IARInvoiceHistoryReportDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ILeaseArHistoryRow {
    id: string;
    clientNo?: number;
    projectNo?: number;
    invoiceNo?: string;
    description?: string;
    invoiceAmount?: number;
    invoiceDate?: string;
    paidBy?: string;
    paidNo?: string;
    paidDate?: string;
    amountPaid?: number;
    balance?: number;
    close?: string;
}

const PAGE_SIZE = 25;

const INVOICE_STATUS_OPTIONS = [
    { value: '1', label: 'Paid Invoices' },
    { value: '2', label: 'Unpaid Invoices' },
    { value: '3', label: 'Paid/Unpaid Invoices' },
];

const SORT_TYPE_OPTIONS = [
    { value: '1', label: 'Invoice Date' },
    { value: '2', label: 'Invoice Number' },
    { value: '3', label: 'Client Number, Project Number' },
];

const formatDateToMMDDYYYY = (value: string): string => {
    if (!value) {
        return '';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
        return value;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatCurrency = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(parsed)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(parsed);
};

const getDefaultDateRange = (): { begin: string; end: string } => {
    const today = new Date();
    const begin = `${today.getFullYear()}-01-01`;
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const end = `${today.getFullYear()}-${month}-${day}`;

    return { begin, end };
};

const ARInvoiceHistoryReportDialog: React.FC<IARInvoiceHistoryReportDialogProps> = ({ isOpen, onClose }) => {
    const { get, response } = useFetch();

    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [beginDate, setBeginDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [invoiceStatus, setInvoiceStatus] = useState('3');
    const [sortType, setSortType] = useState('1');

    const [historyRows, setHistoryRows] = useState<PagedResultSet<ILeaseArHistoryRow> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(false);
    const [isQuerying, setIsQuerying] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
    const [selectedEntryNo, setSelectedEntryNo] = useState('');
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    // The SSRS query accepts a blank invoice status for paid/unpaid-all.
    const reportInvoiceStatus = invoiceStatus === '3' ? '' : invoiceStatus;

    const columns: ITableColumn[] = [
        { name: 'Client', mapping: 'clientNo' },
        { name: 'Pr. No.', mapping: 'projectNo' },
        { name: 'Invoice', mapping: 'invoiceNo' },
        { name: 'Description', mapping: 'description' },
        { name: 'Inv. Amount', mapping: 'invoiceAmount', format: formatCurrency },
        { name: 'Inv. Date', mapping: 'invoiceDate', format: formatDateForDisplay },
        {
            name: 'Paid By',
            mapping: 'paidBy',
            onCellClick: (_row, value) => {
                const normalized = (value || '').trim();
                if (!normalized || normalized === '0') {
                    return;
                }

                setSelectedEntryNo(normalized);
                setIsPaymentHistoryOpen(true);
            },
        },
        { name: '#', mapping: 'paidNo' },
        { name: 'Paid Date', mapping: 'paidDate', format: formatDateForDisplay },
        { name: 'Amount Paid', mapping: 'amountPaid', format: formatCurrency },
        { name: 'Balance', mapping: 'balance', format: formatCurrency },
    ];

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'id',
        columns,
    };

    const loadHistory = useCallback(async (): Promise<void> => {
        setIsQuerying(true);
        setRequestError(null);

        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (clientNo.trim()) {
                params.append('clNo', clientNo.trim());
            }

            if (projectNo.trim()) {
                params.append('prNo', projectNo.trim());
            }

            if (beginDate) {
                params.append('begDate', formatDateToMMDDYYYY(beginDate));
            }

            if (endDate) {
                params.append('endDate', formatDateToMMDDYYYY(endDate));
            }

            params.append('invoiceStatus', reportInvoiceStatus);
            params.append('sortType', sortType);

            const data = await get(`/v1.0/leases/admin/ar-invoice-history?${params.toString()}`);

            if (!response.ok || !data) {
                setRequestError(
                    parseLeaseRecordsApiError(response.data || { title: 'Failed to query A/R Invoice History records.' }, response.status)
                );
                setHistoryRows(undefined);
                return;
            }

            const items: ILeaseArHistoryRow[] = (data.items || []).map((row: ILeaseArHistoryRow, index: number) => ({
                ...row,
                id: `${row.clientNo || ''}-${row.projectNo || ''}-${row.invoiceNo || ''}-${skip + index}`,
            }));

            const paged = new PagedResultSet<ILeaseArHistoryRow>();
            paged.items = items;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setHistoryRows(paged);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error querying A/R Invoice History records.' }, 500));
        } finally {
            setIsQuerying(false);
        }
    }, [beginDate, clientNo, endDate, get, pageNumber, projectNo, reportInvoiceStatus, response, sortType]);

    useEffect(() => {
        if (!isOpen) {
            setIsPaymentHistoryOpen(false);
            setSelectedEntryNo('');
            return;
        }

        if (!beginDate || !endDate) {
            const defaults = getDefaultDateRange();
            setBeginDate(defaults.begin);
            setEndDate(defaults.end);
        }

        setPageNumber(1);
        setShouldReload(true);
    }, [isOpen, beginDate, endDate]);

    useEffect((): void | (() => void) => {
        if (!isOpen) {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !shouldReload) {
            return;
        }

        void loadHistory();
        setShouldReload(false);
    }, [isOpen, shouldReload, loadHistory]);

    useEffect(() => {
        let timeoutId: number | undefined;

        if (isOpen) {
            timeoutId = window.setTimeout(() => {
                setPageNumber(1);
                setShouldReload(true);
            }, 350);
        }

        return () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [isOpen, clientNo, projectNo, beginDate, endDate, invoiceStatus, sortType]);

    const handleQuery = (): void => {
        setPageNumber(1);
        setShouldReload(true);
    };

    const handlePageChange = (newPage: number): void => {
        setPageNumber(newPage);
        setShouldReload(true);
    };

    const handleDownload = async (): Promise<void> => {
        setIsGenerating(true);
        setRequestError(null);

        try {
            const params = new URLSearchParams();

            if (clientNo.trim()) {
                params.append('clNo', clientNo.trim());
            }

            if (projectNo.trim()) {
                params.append('prNo', projectNo.trim());
            }

            if (beginDate) {
                params.append('begDate', formatDateToMMDDYYYY(beginDate));
            }

            if (endDate) {
                params.append('endDate', formatDateToMMDDYYYY(endDate));
            }

            params.append('invoiceStatus', reportInvoiceStatus);
            params.append('sortType', sortType);

            await get(`/v1.0/leases/admin/ar-invoice-history/report?${params.toString()}`);

            if (!response.ok) {
                setRequestError(
                    parseLeaseRecordsApiError(response.data || { title: 'Failed to generate A/R Invoice History report.' }, response.status)
                );
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const fileName = `LeaseARInvoiceHistory_${clientNo.trim()}_${projectNo.trim()}.xlsx`;

            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error downloading report file.' }, 500));
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={onClose}>
            <div style={dialogStyles.content} onClick={(e) => e.stopPropagation()}>
                <div style={dialogStyles.header}>
                    <h3 style={dialogStyles.title}>A/R Invoice History</h3>
                    <button onClick={onClose} style={dialogStyles.closeButton} aria-label="Close report dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.fieldsGrid}>
                        <Field label="Client No." value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                        <Field label="Project No." value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                        <DateField label="Beginning Date" value={beginDate} editable={true} onChange={(value) => setBeginDate(value || '')} />
                        <DateField label="Ending Date" value={endDate} editable={true} onChange={(value) => setEndDate(value || '')} />
                        <SelectField
                            label="Invoice Status"
                            value={invoiceStatus}
                            editable={true}
                            options={INVOICE_STATUS_OPTIONS}
                            onChange={(value) => setInvoiceStatus(value || '3')}
                        />
                        <SelectField
                            label="Sort Type"
                            value={sortType}
                            editable={true}
                            options={SORT_TYPE_OPTIONS}
                            onChange={(value) => setSortType(value || '1')}
                        />
                    </div>

                    <div style={dialogStyles.actionRow}>
                        <button onClick={handleQuery} style={dialogStyles.secondaryButton} disabled={isQuerying || isGenerating}>
                            <div style={dialogStyles.buttonInner}>
                                {isQuerying && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                <span>{isQuerying ? 'Querying...' : 'Query'}</span>
                            </div>
                        </button>
                        <button onClick={handleDownload} style={dialogStyles.primaryButton} disabled={isGenerating || isQuerying}>
                            <div style={dialogStyles.buttonInner}>
                                {isGenerating && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                <span>{isGenerating ? 'Generating...' : 'Download Excel'}</span>
                            </div>
                        </button>
                    </div>

                    <div className="search-leases__table-wrapper ar-invoice-history-report__table-wrapper" style={dialogStyles.tableSection}>
                        <Table
                            isLoading={isQuerying}
                            tableConfig={tableConfig}
                            data={historyRows}
                            enablePaging={true}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>

                <div style={dialogStyles.footer}>
                    <button onClick={onClose} style={dialogStyles.secondaryButton} disabled={isGenerating || isQuerying}>
                        Close
                    </button>
                </div>
            </div>
            <ARPaymentHistoryDialog
                isOpen={isPaymentHistoryOpen}
                entryNo={selectedEntryNo}
                onClose={() => {
                    setIsPaymentHistoryOpen(false);
                    setSelectedEntryNo('');
                }}
            />
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 80,
        padding: '1rem',
    },
    content: {
        width: '95%',
        maxWidth: '1300px',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E5E7EB',
        padding: '1rem 1.25rem',
        backgroundColor: '#F9FAFB',
    },
    title: {
        margin: 0,
        color: '#111827',
        fontSize: '1rem',
        fontWeight: 600,
    },
    closeButton: {
        color: '#6B7280',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        overflow: 'hidden',
        minHeight: 0,
    },
    fieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.75rem',
    },
    hintText: {
        fontSize: '0.8rem',
        color: '#4B5563',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    tableSection: {
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '48vh',
        minHeight: '200px',
    },
    footer: {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        padding: '0.75rem 1.25rem',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    primaryButton: {
        border: 'none',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 600,
        cursor: 'pointer',
    },
    secondaryButton: {
        border: '1px solid #D1D5DB',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 500,
        cursor: 'pointer',
    },
    buttonInner: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
};

export default ARInvoiceHistoryReportDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ARInvoiceHistoryReportDialog.tsx